import { useOrderBookStore } from '@/stores/orderbooksStore'
import { calcPriceLevel } from '@/utils'
import type {
  MarketType,
  PoolReservesResponse,
  WsOrderBookResponse,
  WsSubRequest,
  WsSubResponse,
} from '@/utils/types'
import { computed, ref, type Ref } from 'vue'
import { useWebSocket } from '@/composable/useWebsockets'
import { useAuthStore } from '@/stores/authStore'
import { useMyToast } from '../useMyToast'
import { i18n } from '@/i18n'
import type {
  CexTickerResponse,
  DexPoolChain,
  DexPoolLiveData,
  DexPriceByVolMsg,
  DexScreenerPoolItem,
  DexTvlMsg,
  GmgnPriceResponse,
} from '@/services/dex-pools-v2-service/types'
import { useSpreadBellStore } from '@/stores/spreadBellStore'
import { useSound } from '@vueuse/sound'
import { useFlasherStore } from '@/stores/flasherStore'
import bellSoundFile from '@/assets/sounds/alert.wav'
import { useAutoRefresh } from '../useAutorefresh'
import Decimal from 'decimal.js'
import { useEyeDexStore } from '@/stores/eyeDexStore'
import { DexPoolsV2Service } from '@/services/dex-pools-v2-service'
import { ALLOWED_QUOTE_MINTS } from '@/utils/dexPoolConsts'
import { roundUsdToTier } from '@/utils/roundUsdToTier'
import { capitalize, isNull, throttle } from 'lodash'

const MIN_LIQUIDITY = 5000

export const useCexDexSpread = (
  chain: Ref<string>,
  contract: Ref<string>,
  exchange: Ref<string>,
  asset: Ref<string>,
  marketType: Ref<MarketType>,
  amount: Ref<number>,
  quote: Ref<string> = ref('USDT'),
) => {
  const orderBookStore = useOrderBookStore()
  const { addToast } = useMyToast()
  const bellStore = useSpreadBellStore()
  const bellSound = useSound(bellSoundFile)
  const flasherStore = useFlasherStore()
  const { t } = i18n.global
  const cexLastPrice = ref<number | null>(null)
  const cexLastPriceTs = ref<number | null>(null)
  const dexVol = ref<number | null>(null)
  const dexPrice = ref<number | null>(null)
  const dexTrades = ref<GmgnPriceResponse[]>([])
  const initing = ref(true)
  const loadingLp = ref(true)
  const dexPriceTs = ref<number | null>(null)
  const dexPoolsData = ref<Record<string, DexPoolLiveData>>({})
  const wsAttempts = ref(0)
  const eyeDexStore = useEyeDexStore()
  const restartingByLatency = ref(false)
  const poolReserves = ref<PoolReservesResponse | null>(null)
  const poolReservesTs = ref<number | null>(null)
  const poolSwap = ref('DEX')
  const fetchPoolsActionKey = computed(() => `fetch-pools/${contract.value}`)
  const biggestPool = ref<DexScreenerPoolItem>()
  const poolTokenAddresses = ref<Record<string, { base: string; quote: string }>>({})
  const poolLiquidityUsd = ref<Record<string, number>>({})
  const poolDexMap = ref<Record<string, string>>({})
  let loadingLpTimeout = null as number | null

  // Throttled batch state
  const pendingDexUpdates = new Map<string, Partial<DexPoolLiveData>>()
  const pendingObUpdates = new Map<
    string,
    { exchange: string; marketType: MarketType; asset: string; ob: WsOrderBookResponse }
  >()

  // Memoization caches
  interface PoolCalcRow {
    price_buy: number | null
    price_sell: number | null
    tvl: number | null
    pool: string
    dex: string
    chain_price: number | null
    ts: number
  }
  const poolCalcCache = new Map<string, PoolCalcRow>()
  const obPriceCache = new Map<string, { price: number | null; vol: number | null }>()

  const cexPriceBuyBroken = computed(() => {
    // Если стакан есть, но посчитать цену не удалось
    const orderBook = orderBookStore.get(exchange.value, marketType.value, asset.value)
    return Boolean(orderBook?.asks) ?? isNull(cexPriceBuy.value)
  })

  const cexPriceSellBroken = computed(() => {
    // Если стакан есть, но посчитать цену не удалось
    const orderBook = orderBookStore.get(exchange.value, marketType.value, asset.value)
    return Boolean(orderBook?.bids) ?? isNull(cexPriceSell.value)
  })

  const spread = computed(() => calcSpread(true))
  const backSpread = computed(() => calcSpread(false))

  // Работа с ценами
  const calcSpread = (side: boolean) => {
    if (!dexPrice.value) {
      return
    }
    if (side) {
      if (!cexPriceBuy.value?.price) return
      const dexPriceScoped = gmgnPriceUsed.value ? dexPrice.value : dexPriceSell.value
      if (!dexPriceScoped) return
      return (dexPriceScoped / cexPriceBuy.value?.price - 1) * 100
    } else {
      if (!cexPriceSell.value?.price) return
      const dexPriceScoped = gmgnPriceUsed.value ? dexPrice.value : dexPriceBuy.value
      if (!dexPriceScoped) return
      return (cexPriceSell.value?.price / dexPriceScoped - 1) * 100
    }
  }

  const isSpreadLongable = () => {
    if (!spread.value || !backSpread.value) {
      return null
    }
    return spread.value > backSpread.value
  }

  const getPrice = (side: 'asks' | 'bids') => {
    const orderBook = orderBookStore.get(exchange.value, marketType.value, asset.value)
    if (!orderBook || !amount.value) return null
    const cacheKey =
      `${exchange.value}/${marketType.value}/${asset.value}|${side}|${amount.value}`.toLowerCase()
    const cached = obPriceCache.get(cacheKey)
    if (cached) return cached
    const priceVol = calcPriceLevel(orderBook[side], amount.value)
    if (!priceVol || priceVol.length != 2) {
      const result = { price: null, vol: null }
      obPriceCache.set(cacheKey, result)
      return result
    }
    const result = { price: priceVol[0], vol: priceVol[1] }
    obPriceCache.set(cacheKey, result)
    return result
  }

  const cexPriceBuy = computed(() => getPrice('asks'))
  const cexPriceSell = computed(() => getPrice('bids'))
  const authStore = useAuthStore()

  const cexPriceAuto = computed(() => {
    if (isSpreadLongable()) {
      return cexPriceBuy.value
    } else {
      return cexPriceSell.value
    }
  })

  const dexPriceAuto = computed(() => {
    // return isSpreadLongable() ? dexPriceSell.value : dexPriceBuy.value
    if (isSpreadLongable()) {
      return gmgnPriceUsed.value ? dexPrice.value : dexPriceSell.value
    } else {
      return gmgnPriceUsed.value ? dexPrice.value : dexPriceBuy.value
    }
  })

  const biggestPoolUpdateTs = computed(() => {
    if (compotedDexPools.value.length > 0) {
      return compotedDexPools.value[0]?.ts
    }
    return null
  })
  const compotedDexPools = computed(() => {
    if (poolCalcCache.size > 5000) {
      poolCalcCache.clear()
    }
    const entries = Object.entries(dexPoolsData.value)
      .filter(([, v]) => {
        return v.priceBuy !== null && v.priceSell !== null
      })
      .map(([k, v]) => {
        const cacheKey = `${k}|${v.ts ?? 0}`
        const cached = poolCalcCache.get(cacheKey)
        if (cached) return cached
        const tvl = v.tvl ?? poolLiquidityUsd.value[k] ?? null
        const result: PoolCalcRow = {
          price_buy: v.priceBuy,
          price_sell: v.priceSell,
          tvl,
          pool: k,
          dex: v.dex,
          chain_price: dexPrice.value ?? null,
          ts: v.ts ?? 0,
        }
        poolCalcCache.set(cacheKey, result)
        return result
      })
      .sort((a, b) => (b.tvl ?? 0) - (a.tvl ?? 0))
    const entriesFiltered = entries.filter(
      (v) => (v.tvl ?? poolLiquidityUsd.value[v.pool] ?? 0) >= MIN_LIQUIDITY,
    )
    return entriesFiltered
  })

  const noLiquidityPoolsFound = computed(() => {
    return compotedDexPools.value.length == 0 && !initing.value
  })

  const MAX_POOL_PRICE_DEVIATION = 0.5 // 50%
  const MAX_POOLS_FOR_AVG = 1

  const weightedAvgPrices = computed(() => {
    // Фильтруем пулы с "безумными" ценами (отклонение от CEX > 50%)
    // и берём только топ-3 по TVL для расчёта средней
    const pools = compotedDexPools.value
      .filter((p) => {
        const effectiveTvl = p.tvl ?? poolLiquidityUsd.value[p.pool] ?? 0
        if (effectiveTvl <= MIN_LIQUIDITY) return false
        if (!cexLastPrice.value || !p.price_buy || !p.price_sell) return true
        const devBuy = Math.abs(p.price_buy - cexLastPrice.value) / cexLastPrice.value
        const devSell = Math.abs(p.price_sell - cexLastPrice.value) / cexLastPrice.value
        return (
          devBuy <= MAX_POOL_PRICE_DEVIATION &&
          devSell <= MAX_POOL_PRICE_DEVIATION &&
          p.price_buy &&
          p.price_sell
        )
      })
      .slice(0, MAX_POOLS_FOR_AVG)

    let totalTvlBuy = new Decimal(0)
    let totalTvlSell = new Decimal(0)
    let weightedBuy = new Decimal(0)
    let weightedSell = new Decimal(0)

    for (const p of pools) {
      if (p.price_buy !== null) {
        const weight =
          p.tvl !== null ? new Decimal(p.tvl) : new Decimal(poolLiquidityUsd.value[p.pool] ?? 1)
        totalTvlBuy = totalTvlBuy.add(weight)
        weightedBuy = weightedBuy.add(weight.mul(p.price_buy))
      }
      if (p.price_sell !== null) {
        const weight =
          p.tvl !== null ? new Decimal(p.tvl) : new Decimal(poolLiquidityUsd.value[p.pool] ?? 1)
        totalTvlSell = totalTvlSell.add(weight)
        weightedSell = weightedSell.add(weight.mul(p.price_sell))
      }
    }

    return {
      buy: totalTvlBuy.gt(0) ? weightedBuy.div(totalTvlBuy).toNumber() : null,
      sell: totalTvlSell.gt(0) ? weightedSell.div(totalTvlSell).toNumber() : null,
    }
  })

  const dexPriceBuy = computed(() => weightedAvgPrices.value.buy)
  const dexPriceSell = computed(() => weightedAvgPrices.value.sell)
  // const gmgnPriceUsed = computed(() => {
  //   return dexPriceBuy.value == null || dexPriceSell.value == null
  // })
  const gmgnPriceUsed = computed(() => dexPriceBuy.value === null || dexPriceSell.value === null)

  const flushDexUpdates = throttle(
    () => {
      if (pendingDexUpdates.size === 0) return
      for (const [k, v] of pendingDexUpdates) {
        const existing = dexPoolsData.value[k] ?? {
          pool: k,
          dex: poolDexMap.value[k] ?? 'unknown',
          priceBuy: null,
          priceSell: null,
          tvl: null,
          ts: null,
        }
        dexPoolsData.value[k] = {
          ...existing,
          ...v,
          pool: k,
          dex: existing.dex ?? poolDexMap.value[k] ?? 'unknown',
        }
      }
      pendingDexUpdates.clear()
    },
    500,
    { leading: true, trailing: true },
  )

  const flushObUpdates = throttle(
    () => {
      if (pendingObUpdates.size === 0) return
      for (const { exchange, marketType, asset, ob } of pendingObUpdates.values()) {
        orderBookStore.set(exchange, marketType, asset, ob)
      }
      // invalidate cached price levels for updated orderbooks
      for (const { exchange, marketType, asset } of pendingObUpdates.values()) {
        const base = `${exchange}/${marketType}/${asset}`.toLowerCase()
        obPriceCache.delete(`${base}|asks|${amount.value}`)
        obPriceCache.delete(`${base}|bids|${amount.value}`)
      }
      pendingObUpdates.clear()
    },
    500,
    { leading: true, trailing: true },
  )

  const onClose = (reconnect: () => void) => {
    wsAttempts.value += 1
    if (wsAttempts.value % 5 == 0) {
      addToast({ severity: 'error', message: t(`alerts.connectionLost`) })
    }
    window.setTimeout(() => {
      reconnect()
    }, 1200)
  }

  const onOpen = () => {
    restartingByLatency.value = false
    wsAttempts.value = 0
  }

  const onMessage = async (message: string) => {
    const response: WsSubResponse = JSON.parse(message)
    if (response.event == 'watch') {
      const params = response.params
      if (!params) {
        return
      }
      if (params[0] == 'ob') {
        const marketType = params[1] as MarketType
        const exchange = params[2]
        if (!exchange || !marketType) {
          return
        }
        const data = response.data as WsOrderBookResponse
        pendingObUpdates.set(`${exchange}/${marketType}/${asset.value}`.toLowerCase(), {
          exchange,
          marketType,
          asset: asset.value,
          ob: data,
        })
        flushObUpdates()
      } else if (params[0] == 'dex-gmgn-price') {
        const data = response.data as GmgnPriceResponse
        dexPrice.value = data.price
        dexPriceTs.value = response.ts ?? null
        dexVol.value = data.vol24h
        dexTrades.value.push(data)
        const maxTradesCount = 200
        if (dexTrades.value.length > maxTradesCount) {
          dexTrades.value = dexTrades.value.slice(-maxTradesCount)
        }
      } else if (params[0] == 'ticker') {
        const data = response.data as CexTickerResponse
        cexLastPrice.value = data.price
        cexLastPriceTs.value = response.ts ?? null
      } else if (params[0] == 'pool-reserves') {
        const data = response.data as PoolReservesResponse
        poolReserves.value = data
        poolReservesTs.value = response.ts ?? null
      } else if (params[0] == 'dex-price-by-vol' && params[2]) {
        const poolKey = params[2].toLowerCase()
        const data = response.data as DexPriceByVolMsg
        const update = pendingDexUpdates.get(poolKey) ?? {}
        update.priceBuy = data.buy
        update.priceSell = data.sell
        update.ts = data.ts
        pendingDexUpdates.set(poolKey, update)
        flushDexUpdates()
        loadingLp.value = false
      } else if (params[0] == 'dex-tvl' && params[2]) {
        const poolKey = params[2].toLowerCase()
        const data = response.data as DexTvlMsg
        const update = pendingDexUpdates.get(poolKey) ?? {}
        update.tvl = data.tvl
        if (!update.ts) update.ts = data.ts
        pendingDexUpdates.set(poolKey, update)
        flushDexUpdates()
      }
    }
  }

  const { run, stop } = useWebSocket(
    import.meta.env.VITE_WS_URL + '/api/v2/ws?token=' + authStore.token,
    {
      onMessage: onMessage,
      handleClose: onClose,
      handleOpen: onOpen,
    },
  )

  const startWebSocket = async () => {
    if (!marketType.value || !exchange.value || !asset.value || !contract.value) {
      return
    }

    // Reset pending batches and caches on new session
    pendingDexUpdates.clear()
    pendingObUpdates.clear()
    poolCalcCache.clear()
    obPriceCache.clear()

    initing.value = true
    loadingLp.value = true

    const chainStr = chain.value.toLowerCase() as DexPoolChain
    const amountTier = roundUsdToTier(amount.value)
    const subMessages: Array<WsSubRequest> = [
      {
        action: 'watch',
        params: ['ob', marketType.value, exchange.value, asset.value, quote.value],
      },
      {
        action: 'watch',
        params: ['ticker', exchange.value, marketType.value, asset.value, quote.value],
      },
      {
        action: 'watch',
        params: ['dex-gmgn-price', chainStr, contract.value],
      },
    ]

    const pools = await eyeDexStore.fetchDexScreenerPools(
      fetchPoolsActionKey.value,
      chainStr,
      contract.value,
    )
    const filteredPools = [] as DexScreenerPoolItem[]
    pools?.forEach((v) => {
      console.log(`${v.pair_address}: ${v.liquidity_usd}`)
      if ((v.liquidity_usd ?? 0) <= 1) {
        return
      }
      const allowedMints = ALLOWED_QUOTE_MINTS[chainStr]
      if (allowedMints) {
        const token_a = v.base_token_address
        const token_b = v.quote_token_address
        const isAllowed = (addr: string | null | undefined) =>
          addr && allowedMints.has(addr.toLowerCase())
        if (!isAllowed(token_a) && !isAllowed(token_b)) {
          return
        }
      }
      filteredPools.push(v)
      poolDexMap.value[v.pair_address.toLowerCase()] = v.dex_id
      poolTokenAddresses.value[v.pair_address.toLowerCase()] = {
        base: v.base_token_address ?? '',
        quote: v.quote_token_address ?? '',
      }
      poolLiquidityUsd.value[v.pair_address.toLowerCase()] = v.liquidity_usd ?? 0
      subMessages.push({
        action: 'watch',
        params: ['dex-price-by-vol', chainStr, v.pair_address, amountTier],
      })
      subMessages.push({
        action: 'watch',
        params: ['dex-tvl', chainStr, v.pair_address],
      })
    })
    filteredPools.sort((a, b) => (b.liquidity_usd ?? 0) - (a.liquidity_usd ?? 0))
    poolSwap.value = capitalize(filteredPools[0]?.dex_id.split('-')[0]) ?? 'DEX'
    biggestPool.value = filteredPools[0]

    // Инициализация метаданных V4-пулов в defi-watcher
    // Uniswap V4 / Pancake V4 используют 66-символьный poolId (0x + 64 hex)
    // в отличие от обычных EVM-адресов контрактов (0x + 40 hex = 42 символа)
    const v4Chains = new Set<DexPoolChain>(['eth', 'base', 'bsc'])
    if (v4Chains.has(chainStr)) {
      filteredPools.forEach((pool) => {
        const addr = pool.pair_address.toLowerCase()
        if (addr.startsWith('0x') && addr.length === 66) {
          void DexPoolsV2Service.putV4PoolMeta(chainStr, pool.pair_address).catch(() => {
            // fire-and-forget: не блокируем инициализацию WebSocket
          })
        }
      })
    }

    const msg = JSON.stringify(subMessages)
    run(msg)
    initing.value = false

    if (loadingLpTimeout) {
      window.clearInterval(loadingLpTimeout)
    }
    loadingLpTimeout = window.setTimeout(() => {
      if (loadingLp.value) {
        addToast({ severity: 'error', message: t(`alerts.lpTimedOut`) })
        loadingLp.value = false
      }
    }, 15000)
  }

  const checkBell = (v: number | null | undefined, source: number | null | undefined) => {
    if (v && source && v >= source) {
      bellSound.play()
      flasherStore.start()
    }
  }

  const checkBellRefresher = useAutoRefresh(() => {
    checkBell(spread.value, bellStore.spread)
    checkBell(backSpread.value, bellStore.backSpread)
  }, 1000)

  return {
    checkBellRefresher,
    stopWebSocket: stop,
    startWebSocket,
    cexPriceBuyBroken,
    cexPriceSellBroken,
    cexLastPrice,
    cexLastPriceTs,
    dexPrice,
    dexPriceTs,
    dexTrades,
    calcSpread,
    spread,
    isSpreadLongable,
    backSpread,
    cexPriceAuto,
    getPrice,
    biggestPoolUpdateTs,
    cexPriceBuy,
    dexPriceAuto,
    poolSwap,
    cexPriceSell,
    loadingLp,
    dexVol,
    biggestPool,
    poolReserves,
    poolReservesTs,
    dexPoolsData,
    compotedDexPools,
    dexPriceBuy,
    dexPriceSell,
    gmgnPriceUsed,
    noLiquidityPoolsFound,
  }
}
